import React, { useState, useCallback } from 'react';
import {
  Button,
  Input,
  Card,
  Row,
  Col,
  Form,
  message,
  Skeleton,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import OpportunitiesTable from '../../components/sales/OpportunitiesTable';
import type { Contact, Money, Lead, Activity } from '../../types';
import OpportunityModal from '../../components/sales/OpportunityModal';
import ConfirmDeleteModal from '../../components/modal/ConfirmDeleteModal';
import { getLeads, addOrUpdateOpportunity, removeOpportunity } from '../../api/leads';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_STALE_TIME_MS } from '../../constants/api';
import dayjs from 'dayjs';

interface OpportunityFormData {
  lead_id?: string;
  stage: string;
  price: Money;
  units: number;
  follow_up_date: any;
  contacts: Contact[];
  activity_type?: string;
  activity_summary?: string;
}

const OpportunitiesPage: React.FC = () => {
  const { t } = useTranslation(['common', 'sales']);
  const navigate = useNavigate();
  const [form] = Form.useForm<OpportunityFormData>();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingOpportunity, setEditingOpportunity] = useState<Lead | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLeadId, ] = useState<string | undefined>(undefined);
  const queryClient = useQueryClient();

  // Fetch all leads
  const { data: leads = [], isLoading } = useQuery({
    queryKey: ['leads', 'all'],
    queryFn: async () => {
      const { leads } = await getLeads(100);
      return leads;
    },
    staleTime: API_STALE_TIME_MS,
  });

  // Only show leads with an opportunity
  const filteredLeads = leads.filter(lead => !!lead.opportunity);

  // Opportunity add/edit mutation
  const opportunityMutation = useMutation({
    mutationFn: ({ leadId, opportunity }: { leadId: string; opportunity: any }) => addOrUpdateOpportunity(leadId, opportunity),
    onSuccess: () => {
      message.success(t('success:updateSuccess', { entity: t('opportunities.opportunity') }));
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      setOpenDialog(false);
    },
    onError: () => {
      message.error(t('errors.saveFailed', { entity: t('opportunities.opportunity') }));
    },
  });

  // Opportunity delete mutation
  const deleteOpportunityMutation = useMutation({
    mutationFn: (leadId: string) => removeOpportunity(leadId),
    onSuccess: () => {
      message.success(t('success:deleteSuccess', { entity: t('opportunities.opportunity') }));
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      setOpenDeleteDialog(false);
    },
    onError: () => {
      message.error(t('errors.deleteFailed', { entity: t('opportunities.opportunity') }));
    },
  });

  const handleRowClick = useCallback((lead: Lead) => {
    navigate(`/sales/opportunities/${lead.id}`, { state: { lead } });
  }, [navigate]);

  const handleAdd = useCallback(() => {
    setEditingOpportunity(null);
    form.resetFields();
    setOpenDialog(true);
  }, [form]);

  const handleEdit = useCallback((lead: Lead) => {
    setEditingOpportunity(lead);
    form.setFieldsValue({
      stage: lead.opportunity?.stage,
      price: lead.opportunity?.value,
      units: lead.opportunity?.units,
      follow_up_date: dayjs(lead.opportunity?.expected_close_date),
    });
    setOpenDialog(true);
  }, [form]);

  const handleDelete = useCallback((lead: Lead) => {
    setDeleteId(lead.id);
    setOpenDeleteDialog(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (deleteId) {
      deleteOpportunityMutation.mutate(deleteId);
      setDeleteId(null);
    }
  }, [deleteId, deleteOpportunityMutation]);

  const handleCancelDelete = useCallback(() => {
    setOpenDeleteDialog(false);
    setDeleteId(null);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false);
    setEditingOpportunity(null);
    form.resetFields();
  }, [form]);

  // Handler for search input
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value), []);

  // Save handler for OpportunityModal
  const handleSave = async (values: OpportunityFormData) => {
    try {
      const now = new Date();
      // Only include value if present
      const opportunity: any = {
        stage: values.stage,
        expected_close_date: values.follow_up_date instanceof Date ? values.follow_up_date : values.follow_up_date.toDate(),
        units: values.units,
      };
      if (values.price !== undefined && values.price !== null) {
        opportunity.value = values.price;
      }
      // Create a new Activity if activity fields are provided
      let newActivity: Activity | undefined;
      if (values.activity_type && values.activity_summary) {
        newActivity = {
          id: Date.now().toString(),
          type: values.activity_type as any,
          summary: values.activity_summary,
          date: now,
          performed_by: 'Current User', // TODO: Replace with actual user
          outcome: 'Completed',
          created_at: now,
        };
      }
      if (editingOpportunity) {
        await opportunityMutation.mutateAsync({ leadId: editingOpportunity.id, opportunity: { ...opportunity, activities: newActivity ? [newActivity] : [] } });
      } else if (values.lead_id) {
        await opportunityMutation.mutateAsync({ leadId: values.lead_id, opportunity: { ...opportunity, activities: newActivity ? [newActivity] : [] } });
      }
    } catch (error) {
      // Rethrow to OpportunityModal for alert
      throw error;
    }
  };

  // Filtered opportunities by search term (owner or name)
  const filteredOpportunities = filteredLeads.filter((lead: Lead) =>
    lead.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div style={{ width: '100%' }}>
      {isLoading && filteredOpportunities.length === 0 ? (
        <Card style={{ marginTop: 24 }}><Skeleton active /></Card>
      ) : (
        <>
        <Row 
          justify="space-between" 
          align="middle" 
          style={{ marginBottom: 24, marginTop: 24}}
          gutter={[{ xs: 8, sm: 16 }, { xs: 8, sm: 16 }]}
        >
          <Col xs={24} sm={16} md={18}>
            <Input
              placeholder={t('sales:opportunities.searchPlaceholder')}
              value={searchTerm}
              onChange={handleSearchChange}
              prefix={<SearchOutlined />}
              allowClear
            />
          </Col>
          <Col xs={24} sm={8} md={6} style={{ textAlign: 'right' }}>
            <Button
              type="primary"
              onClick={handleAdd}
              icon={<PlusOutlined />}
            >
              {t('sales:opportunities.add')}
            </Button>
          </Col>
        </Row>

        <Card variant="borderless" style={{ boxShadow: '0 0 10px rgba(0,0,0,0.1)' }}>
          <OpportunitiesTable
            opportunities={filteredOpportunities}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onView={handleRowClick}
            loading={isLoading}
            leads={leads}
          />
        </Card>
        </>
      )}

      <OpportunityModal
        open={openDialog}
        onOk={handleSave}
        onCancel={handleCloseDialog}
        form={form}
        leads={leads}
        editingOpportunity={editingOpportunity}
        defaultLeadId={selectedLeadId}
      />

      <ConfirmDeleteModal
        open={openDeleteDialog}
        onOk={handleConfirmDelete}
        onCancel={handleCancelDelete}
        title={t('common:confirmDelete')}
        description={t('sales:opportunities.deleteConfirm')}
        okText={t('common:delete')}
        cancelText={t('common:cancel')}
        okButtonProps={{ danger: true }}
      />
    </div>
  );
};

export default OpportunitiesPage; 